import React, { useState, useEffect } from 'react';
import { Button, Input, Select, SelectItem, Modal, ModalContent, ModalHeader, ModalBody, ModalFooter, useDisclosure } from '@nextui-org/react';
import { FiPlus, FiEdit2, FiTrash2, FiCalendar, FiClock, FiUser, FiFlag, FiLoader } from 'react-icons/fi';
import { format, addDays, differenceInDays, startOfDay, endOfDay } from 'date-fns';
import { tr } from 'date-fns/locale';
import { 
  getGanttTasks, 
  createGanttTask, 
  updateGanttTask, 
  deleteGanttTask,
  formatTaskForApi,
  formatTaskFromApi
} from '../../../services/gantt';

const GanttComponent = ({ projectId }) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [tasks, setTasks] = useState([]);
  const [editingTask, setEditingTask] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newTask, setNewTask] = useState({
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    progress: 0,
    priority: 'medium',
    assignee: '',
    assignedUserId: null
  });
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('day'); // day, week, month
  const [scrollPosition, setScrollPosition] = useState(0);

  // API'den görevleri yükle
  useEffect(() => {
    const fetchTasks = async () => {
      if (!projectId) return;
      
      try {
        setLoading(true);
        setError(null);
        const response = await getGanttTasks(projectId);
        const formattedTasks = response.tasks.map(formatTaskFromApi);
        setTasks(formattedTasks);
      } catch (err) {
        setError('Görevler yüklenirken bir hata oluştu.');
        setTasks([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [projectId]);

  const priorityColors = {
    high: 'bg-red-500',
    medium: 'bg-yellow-500',
    low: 'bg-green-500'
  };

  const statusColors = {
    completed: 'bg-green-100 dark:bg-green-900/20 border-green-300 dark:border-green-700',
    'in-progress': 'bg-blue-100 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700',
    pending: 'bg-gray-100 dark:bg-gray-700 border-gray-300 dark:border-gray-600'
  };

  const getTaskDuration = (startDate, endDate) => {
    // Tarih string'lerini parse et
    const startParts = startDate.split('-');
    const endParts = endDate.split('-');
    
    const startYear = parseInt(startParts[0]);
    const startMonth = parseInt(startParts[1]);
    const startDay = parseInt(startParts[2]);
    
    const endYear = parseInt(endParts[0]);
    const endMonth = parseInt(endParts[1]);
    const endDay = parseInt(endParts[2]);
    
    // Tarihleri doğru şekilde oluştur (timezone sorunlarını önlemek için 12:00:00 ekle)
    const start = new Date(startYear, startMonth - 1, startDay, 12, 0, 0);
    const end = new Date(endYear, endMonth - 1, endDay, 12, 0, 0);
    
    // Farkı hesapla
    const timeDiff = end.getTime() - start.getTime();
    const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
    
    return daysDiff;
  };

  const getTaskPosition = (startDate, projectStart) => {
    // Tarih string'lerini parse et
    const startParts = startDate.split('-');
    const projectParts = projectStart.toISOString().split('T')[0].split('-');
    
    const startYear = parseInt(startParts[0]);
    const startMonth = parseInt(startParts[1]);
    const startDay = parseInt(startParts[2]);
    
    const projectYear = parseInt(projectParts[0]);
    const projectMonth = parseInt(projectParts[1]);
    const projectDay = parseInt(projectParts[2]);
    
    // Tarihleri doğru şekilde oluştur (timezone sorunlarını önlemek için 12:00:00 ekle)
    const start = new Date(startYear, startMonth - 1, startDay, 12, 0, 0);
    const projectStartDate = new Date(projectYear, projectMonth - 1, projectDay, 12, 0, 0);
    
    // Farkı hesapla
    const timeDiff = start.getTime() - projectStartDate.getTime();
    const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));
    
    return daysDiff;
  };

  const getProjectDateRange = () => {
    if (tasks.length === 0) return { start: new Date(), end: addDays(new Date(), 30) };
    
    const dates = tasks.flatMap(task => [
      new Date(task.startDate + 'T12:00:00'), 
      new Date(task.endDate + 'T12:00:00')
    ]);
    const start = new Date(Math.min(...dates));
    const end = new Date(Math.max(...dates));
    
    return { start, end };
  };

  const generateDateHeaders = (startDate, endDate) => {
    const headers = [];
    const current = new Date(startDate);
    
    if (viewMode === 'day') {
      // Günlük görünüm - her gün
      while (current <= endDate) {
        headers.push(new Date(current));
        current.setDate(current.getDate() + 1);
      }
    } else if (viewMode === 'month') {
      // Aylık görünüm - her ay
      while (current <= endDate) {
        headers.push(new Date(current));
        current.setMonth(current.getMonth() + 1);
      }
    }
    
    return headers;
  };

  const handleAddTask = () => {
    setEditingTask(null);
    setNewTask({
      name: '',
      description: '',
      startDate: '',
      endDate: '',
      progress: 0,
      priority: 'medium',
      assignee: '',
      assignedUserId: null
    });
    onOpen();
  };

  const handleEditTask = (task) => {
    setEditingTask(task);
    setNewTask({
      ...task,
      assignedUserId: task.assignedUserId || null,
      // Tarih formatını HTML input için düzenle
      startDate: task.startDate ? task.startDate.split('T')[0] : '',
      endDate: task.endDate ? task.endDate.split('T')[0] : ''
    });
    onOpen();
  };

  const handleSaveTask = async () => {
    try {
      const taskData = formatTaskForApi(newTask);
      
      if (editingTask) {
        // Görev güncelle
        const response = await updateGanttTask(editingTask.id, taskData);
        const updatedTask = formatTaskFromApi(response.task);
        setTasks(prev => 
          prev.map(t => t.id === editingTask.id ? updatedTask : t)
        );
      } else {
        // Yeni görev oluştur
        const response = await createGanttTask(projectId, taskData);
        const newTask = formatTaskFromApi(response.task);
        setTasks(prev => [...prev, newTask]);
      }
      onClose();
    } catch (err) {
      setError('Görev kaydedilirken bir hata oluştu.');
    }
  };

  const handleDeleteTask = async (id) => {
    try {
      await deleteGanttTask(id);
      setTasks(prev => prev.filter(t => t.id !== id));
    } catch (err) {
      setError('Görev silinirken bir hata oluştu.');
    }
  };

  const handleInputChange = (field, value) => {
    setNewTask(prev => ({ ...prev, [field]: value }));
  };

  // Scroll senkronizasyonu için event handler'lar
  const handleHeaderScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;
    const maxScrollLeft = e.target.scrollWidth - e.target.clientWidth;
    setScrollPosition(scrollLeft);
    
    // Görev çubukları container'ını da aynı pozisyona scroll et
    const taskContainers = document.querySelectorAll('.gantt-tasks-container');
    taskContainers.forEach(container => {
      if (container.scrollLeft !== scrollLeft) {
        container.scrollLeft = Math.min(scrollLeft, maxScrollLeft);
      }
    });
  };

  const handleTasksScroll = (e) => {
    const scrollLeft = e.target.scrollLeft;
    setScrollPosition(scrollLeft);
    
    // Tarih başlıkları container'ını da aynı pozisyona scroll et
    const headerContainer = document.querySelector('.gantt-header-container');
    if (headerContainer && headerContainer.scrollLeft !== scrollLeft) {
      headerContainer.scrollLeft = scrollLeft;
    }
  };

  const { start: projectStart, end: projectEnd } = getProjectDateRange();
  const dateHeaders = generateDateHeaders(projectStart, projectEnd);

  // Loading durumu
  if (loading) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-white dark:bg-dark-300">
        <FiLoader className="w-8 h-8 animate-spin text-gray-400 mb-4" />
        <p className="text-gray-600 dark:text-gray-400">Görevler yükleniyor...</p>
      </div>
    );
  }

  // Error durumu
  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center bg-white dark:bg-dark-300">
        <div className="text-center">
          <FiCalendar className="w-16 h-16 text-red-400 mb-4 mx-auto" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Hata Oluştu</h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">{error}</p>
          <Button
            color="primary"
            onClick={() => window.location.reload()}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            Tekrar Dene
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-dark-300">
      {/* Header */}
      <div className="bg-gray-50 dark:bg-dark-300 border-b border-gray-200 dark:border-gray-600 p-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Gantt Şeması</h2>
            <p className="text-sm text-gray-600 dark:text-gray-400">Proje görevlerinin zaman çizelgesi</p>
          </div>
          <div className="flex space-x-3">
            <Select
              size="sm"
              placeholder="Görünüm"
              selectedKeys={[viewMode]}
              onSelectionChange={(keys) => setViewMode(Array.from(keys)[0])}
              className="w-32"
              classNames={{
                trigger: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                value: "text-gray-900 dark:text-white",
                label: "text-gray-700 dark:text-gray-300"
              }}
            >
              <SelectItem key="day">Günlük</SelectItem>
              <SelectItem key="month">Aylık</SelectItem>
            </Select>
            <Button
              color="primary"
              startContent={<FiPlus />}
              onClick={handleAddTask}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Görev Ekle
            </Button>
          </div>
        </div>
      </div>

      {/* Gantt Chart */}
      <div className="flex-1 overflow-auto bg-white dark:bg-dark-300">
        {tasks.length > 0 ? (
          <div className="min-w-full">
            {/* Date Headers */}
            <div className="sticky top-0 z-10 bg-gray-50 dark:bg-dark-300 border-b border-gray-200 dark:border-gray-600">
              <div className="flex">
                <div className="w-80 p-4 border-r border-gray-200 dark:border-gray-600">
                  <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">Görevler</h3>
                </div>
                <div className="flex-1 overflow-x-auto gantt-header-container" onScroll={handleHeaderScroll} style={{ scrollbarWidth: 'auto' }}>
                  <div className="flex min-w-max">
                    {dateHeaders.map((date, index) => {
                      const getHeaderWidth = () => {
                        switch (viewMode) {
                          case 'day': return 'w-20'; // 80px
                          case 'month': return 'w-40'; // 160px
                          default: return 'w-20';
                        }
                      };

                      const getHeaderContent = () => {
                        switch (viewMode) {
                          case 'day':
                            return (
                              <>
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  {format(date, 'dd', { locale: tr })}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-500">
                                  {format(date, 'MMM', { locale: tr })}
                                </div>
                              </>
                            );
                          case 'month':
                            return (
                              <>
                                <div className="text-xs text-gray-600 dark:text-gray-400">
                                  {format(date, 'MMM yyyy', { locale: tr })}
                                </div>
                                <div className="text-xs text-gray-500 dark:text-gray-500">
                                  {format(date, 'yyyy', { locale: tr })}
                                </div>
                              </>
                            );
                          default:
                            return null;
                        }
                      };

                      return (
                        <div
                          key={index}
                          className={`${getHeaderWidth()} p-2 text-center border-r border-gray-200 dark:border-gray-600`}
                        >
                          {getHeaderContent()}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>

            {/* Tasks */}
            <div className="divide-y divide-gray-200 dark:divide-gray-600">
              {tasks.map((task) => {
                // ViewMode'a göre süre ve pozisyon hesapla
                 const getTaskDurationForView = (startDate, endDate) => {
                   if (viewMode === 'day') {
                     return getTaskDuration(startDate, endDate); // Günlük hesaplama
                   } else if (viewMode === 'month') {
                     // Aylık hesaplama - kaç ay
                     const start = new Date(startDate);
                     const end = new Date(endDate);
                     const monthsDiff = (end.getFullYear() - start.getFullYear()) * 12 + (end.getMonth() - start.getMonth()) + 1;
                     return monthsDiff;
                   }
                   return getTaskDuration(startDate, endDate);
                 };

                 const getTaskPositionForView = (startDate, projectStart) => {
                   if (viewMode === 'day') {
                     return getTaskPosition(startDate, projectStart); // Günlük hesaplama
                   } else if (viewMode === 'month') {
                     // Aylık hesaplama - kaç ay sonra
                     const start = new Date(startDate);
                     const projectStartDate = new Date(projectStart);
                     const monthsDiff = (start.getFullYear() - projectStartDate.getFullYear()) * 12 + (start.getMonth() - projectStartDate.getMonth());
                     return monthsDiff;
                   }
                   return getTaskPosition(startDate, projectStart);
                 };

                const duration = getTaskDurationForView(task.startDate, task.endDate);
                const position = getTaskPositionForView(task.startDate, projectStart);
                
                 // ViewMode'a göre genişlik ve pozisyon hesapla
                 const getPixelPerUnit = () => {
                   switch (viewMode) {
                     case 'day': return 88; // 88px per day
                     case 'month': return 160; // 160px per month
                     default: return 88;
                   }
                 };

                 const pixelPerUnit = getPixelPerUnit();
                 const width = duration * pixelPerUnit;
                 const left = position * pixelPerUnit;

                 // Scroll pozisyonuna göre görev çubuğunun görünürlüğünü kontrol et
                 const isTaskVisible = () => {
                   const taskStart = left;
                   const taskEnd = left + width;
                   const scrollStart = scrollPosition;
                   
                   // Container genişliğini dinamik olarak hesapla
                   const containerWidth = window.innerWidth - 320; // 320px = sol panel genişliği
                   const scrollEnd = scrollStart + containerWidth;
                   
                   // Görev çubuğu scroll alanının içindeyse görünür
                   return !(taskEnd < scrollStart || taskStart > scrollEnd);
                 };

                 const isVisible = isTaskVisible();




                return (
                  <div key={task.id} className="flex min-h-[80px] hover:bg-gray-50 dark:hover:bg-gray-600/50">
                    {/* Task Info */}
                    <div className="w-80 p-4 border-r border-gray-200 dark:border-gray-600 flex items-center">
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-1">
                          <h4 className="text-sm font-medium text-gray-900 dark:text-white">{task.name}</h4>
                          <div className={`w-2 h-2 rounded-full ${priorityColors[task.priority]}`}></div>
                        </div>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mb-1">{task.description}</p>
                        <div className="flex items-center space-x-4 text-xs text-gray-500 dark:text-gray-400">
                          <span className="flex items-center">
                            <FiUser className="w-3 h-3 mr-1" />
                            {task.assignee}
                          </span>
                          <span className="flex items-center">
                            <FiFlag className="w-3 h-3 mr-1" />
                            {task.progress}%
                          </span>
                        </div>
                      </div>
                      <div className="flex space-x-1 ml-2">
                        <button
                          onClick={() => handleEditTask(task)}
                          className="p-1 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        >
                          <FiEdit2 size={14} />
                        </button>
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          className="p-1 text-gray-400 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </div>

                    {/* Gantt Bar */}
                    <div 
                      className="flex-1 relative p-2 overflow-x-auto gantt-tasks-container hide-scrollbar" 
                      onScroll={handleTasksScroll}
                    >
                      <div className="relative h-12" style={{ 
                        width: '100%',
                        minWidth: `${width + left + 100}px`
                      }}>
                         <div
                           className={`absolute top-1 h-10 rounded-md border-2 ${statusColors[task.status]} flex items-center justify-center transition-opacity duration-200`}
                           style={{
                             left: `${left}px`,
                             width: `${width}px`,
                             minWidth: `${width}px`,
                             maxWidth: `${width}px`,
                             transform: 'none',
                             opacity: isVisible ? 1 : 0,
                             visibility: isVisible ? 'visible' : 'hidden'
                           }}
                         >
                          <div 
                            className={`font-medium text-gray-700 dark:text-gray-300 ${
                              viewMode === 'day' ? 'text-xs px-2' : 
                              'text-sm px-4'
                            }`}
                            style={{
                              textAlign: 'center',
                              width: '100%',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <div className="font-semibold truncate w-full">
                              {task.name}
                            </div>
                            <div className={`${viewMode === 'day' ? 'text-xs' : 'text-xs'} opacity-75 truncate w-full`}>
                              {format(new Date(task.startDate), 'dd MMM', { locale: tr })} - {format(new Date(task.endDate), 'dd MMM', { locale: tr })}
                            </div>
                          </div>
                          {/* Progress Bar */}
                          <div
                            className="absolute top-0 left-0 h-full bg-green-500 rounded-l-md opacity-30"
                            style={{ width: `${task.progress}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-center p-8">
            <FiCalendar className="w-16 h-16 text-gray-300 dark:text-gray-600 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">Henüz görev yok</h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">İlk görevinizi ekleyerek başlayın</p>
            <Button
              color="primary"
              startContent={<FiPlus />}
              onClick={handleAddTask}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Görev Ekle
            </Button>
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalContent>
          <ModalHeader>
            {editingTask ? 'Görev Düzenle' : 'Yeni Görev'}
          </ModalHeader>
          <ModalBody>
            <div className="space-y-4">
              <Input
                label="Görev Adı"
                placeholder="Görev adını girin"
                value={newTask.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                classNames={{
                  inputWrapper: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                  input: "text-gray-900 dark:text-white",
                  label: "text-gray-700 dark:text-gray-300"
                }}
              />
              
              <Input
                label="Açıklama"
                placeholder="Görev açıklaması"
                value={newTask.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                classNames={{
                  inputWrapper: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                  input: "text-gray-900 dark:text-white",
                  label: "text-gray-700 dark:text-gray-300"
                }}
              />
              
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="date"
                  label="Başlangıç Tarihi"
                  value={newTask.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  classNames={{
                    inputWrapper: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                    input: "text-gray-900 dark:text-white",
                    label: "text-gray-700 dark:text-gray-300"
                  }}
                />
                
                <Input
                  type="date"
                  label="Bitiş Tarihi"
                  value={newTask.endDate}
                  onChange={(e) => handleInputChange('endDate', e.target.value)}
                  classNames={{
                    inputWrapper: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                    input: "text-gray-900 dark:text-white",
                    label: "text-gray-700 dark:text-gray-300"
                  }}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <Input
                  type="number"
                  label="İlerleme (%)"
                  min="0"
                  max="100"
                  value={newTask.progress}
                  onChange={(e) => handleInputChange('progress', parseInt(e.target.value) || 0)}
                  classNames={{
                    inputWrapper: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                    input: "text-gray-900 dark:text-white",
                    label: "text-gray-700 dark:text-gray-300"
                  }}
                />
                
                <Select
                  label="Öncelik"
                  placeholder="Öncelik seçin"
                  selectedKeys={[newTask.priority]}
                  onSelectionChange={(keys) => handleInputChange('priority', Array.from(keys)[0])}
                  classNames={{
                    trigger: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                    value: "text-gray-900 dark:text-white",
                    label: "text-gray-700 dark:text-gray-300"
                  }}
                >
                  <SelectItem key="high">Yüksek</SelectItem>
                  <SelectItem key="medium">Orta</SelectItem>
                  <SelectItem key="low">Düşük</SelectItem>
                </Select>
              </div>
              
              <Input
                label="Atanan Kişi"
                placeholder="Görev atanan kişi"
                value={newTask.assignee}
                onChange={(e) => handleInputChange('assignee', e.target.value)}
                classNames={{
                  inputWrapper: "bg-white dark:bg-dark-300 border-gray-300 dark:border-gray-600",
                  input: "text-gray-900 dark:text-white",
                  label: "text-gray-700 dark:text-gray-300"
                }}
              />
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={onClose}>
              İptal
            </Button>
            <Button 
              color="primary" 
              onPress={handleSaveTask}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {editingTask ? 'Güncelle' : 'Ekle'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default GanttComponent;
